import { Fragment, useEffect, useRef, useState } from 'react';
import {
  getContratById,
  modifierContrat,
  urlApercuContrat,
  urlPdfContrat,
  regenererPdfContrat,
  appliquerModeleContrat,
  telechargerDocxContrat,
  importerDocxContrat,
  retirerDocxContrat,
} from '../services/contratService';
import { getModelesContrat } from '../services/modeleContratService';
import { formatDate } from './devisUtils';
import SelecteurModeleContrat from './SelecteurModeleContrat';
import '../assets/styles/ContratPanneau.css';
// Le pad de signature porte ses styles ici (cf. SignaturePad).
import './Signature.css';
import { IconeTelecharger } from '../components/IconesDocument';
import RemplirParTexte from '../components/RemplirParTexte';

/**
 * Panneau du contrat, ouvert par le bouton « Voir » de la liste.
 *
 * L'aperçu n'est pas une deuxième mise en page du contrat : c'est le gabarit
 * même dont le PDF est tiré, servi par /api/contrats/:id/apercu et affiché
 * dans une iframe. L'écran ne peut donc pas montrer autre chose que ce que le
 * client recevra.
 *
 * « Modifier » bascule le corps du panneau vers les mentions que le PDF
 * imprime en tête : l'entreprise cliente (bloc « Et ») et les mentions
 * propres au contrat. Les montants, durées et prestations sont un
 * instantané du devis : les corriger ici ferait diverger deux documents que
 * le client a tous les deux sous les yeux.
 */

/**
 * `cle` est la colonne envoyée au serveur ; `source`, quand elle diffère,
 * est le champ du contrat lu pour préremplir. Les mentions de l'entreprise
 * ont leur propre colonne sur le contrat (client_nom…), mais la vue renvoie
 * la valeur effective — celle de la fiche contact tant qu'aucune correction
 * n'a été faite — sous le nom que le gabarit lit (societe_nom…).
 */
const SECTIONS = [
  {
    titre: 'Entreprise cliente',
    champs: [
      { cle: 'client_nom', source: 'societe_nom', libelle: 'Nom' },
      { cle: 'client_siret', source: 'siret', libelle: "Siret d'entreprise" },
      { cle: 'client_adresse', source: 'adresse_client', libelle: 'Adresse', multiligne: true },
      { cle: 'sites_concernes', libelle: 'Sites concernés' },
      { cle: 'client_convention', source: 'convention_collective', libelle: 'Convention collective' },
      { cle: 'representant_nom', libelle: 'Représenté par' },
      { cle: 'representant_fonction', libelle: 'Fonction du représentant' },
      { cle: 'interlocuteur_nom', libelle: 'Interlocuteur privilégié' },
      { cle: 'interlocuteur_fonction', libelle: "Fonction de l'interlocuteur" },
      { cle: 'interlocuteur_email', libelle: 'Mail', type: 'email' },
    ],
  },
  {
    titre: 'Contrat',
    champs: [
      { cle: 'date_contrat', libelle: 'Date du contrat', type: 'date' },
      { cle: 'lieu_signature', libelle: 'Lieu de signature' },
      { cle: 'intitule_action', libelle: "Intitulé de l'action", multiligne: true },
      { cle: 'dates_session_texte', libelle: 'Dates de session' },
      { cle: 'lieu_formation', libelle: 'Lieu de formation' },
      { cle: 'format_libelle', libelle: 'Format' },
    ],
  },
];

const CHAMPS = SECTIONS.flatMap((s) => s.champs);

/**
 * Champs proposés à la zone « coller un texte » : ceux des sections, plus
 * l'offre et la TVA. Les prix et remises des lignes en sont exclus : ils sont
 * répétés ligne par ligne, et un texte collé ne dit pas laquelle viser.
 */
const CHAMPS_IA = [
  ...CHAMPS.map((c) => ({ cle: c.cle, libelle: c.libelle, type: c.type || 'texte' })),
  { cle: 'offre_libelle', libelle: 'Offre exceptionnelle (libellé)' },
  { cle: 'offre_ht', libelle: 'Montant de l’offre (€ HT)', type: 'nombre' },
  { cle: 'taux_tva', libelle: 'TVA (%)', type: 'nombre' },
];

/** Une date ISO renvoyée par l'API doit être tronquée pour un <input date>. */
function versInputDate(valeur) {
  if (!valeur) return '';
  const texte = String(valeur);
  return texte.length >= 10 ? texte.slice(0, 10) : texte;
}

function brouillonDepuis(contrat) {
  const b = {};
  for (const champ of CHAMPS) {
    const valeur = contrat[champ.source || champ.cle];
    b[champ.cle] = champ.type === 'date' ? versInputDate(valeur) : (valeur ?? '');
  }
  // Tarifs du contrat (migration 078) : prix, remise et motif de chaque
  // ligne, offre exceptionnelle, TVA. Corrigés ici, ils ne touchent pas le
  // devis : le serveur recalcule les montants du contrat seul.
  b.lignes = ((contrat.donnees && contrat.donnees.lignes) || []).map((l) => ({
    libelle: l.libelle,
    groupe_libelle: l.groupe_libelle,
    unite: l.unite,
    duree_jours: l.duree_jours,
    nb_participants: l.nb_participants,
    prix_unitaire_ht: l.prix_unitaire_ht ?? '',
    taux_remise: l.taux_remise ?? 0,
    remise_journee_ht: l.remise_journee_ht ?? 0,
    motif_remise: l.motif_remise || '',
  }));
  b.offre_libelle = contrat.offre_libelle ?? '';
  b.offre_ht = Number(contrat.offre_ht) > 0 ? Number(contrat.offre_ht) : '';
  b.taux_tva = contrat.taux_tva ?? '';
  return b;
}

/** Aperçu local d'une ligne corrigée : le serveur reste maître des montants. */
function apercuLigneContrat(l) {
  const prix = Number(l.prix_unitaire_ht) || 0;
  const brut =
    l.unite === 'jour_participant'
      ? prix * Number(l.duree_jours || 0) * Number(l.nb_participants || 1)
      : prix * Number(l.duree_jours || 0);
  const joursFactures =
    l.unite === 'jour_participant'
      ? Number(l.duree_jours || 0) * Number(l.nb_participants || 1)
      : Number(l.duree_jours || 0);
  const remise = Math.min(
    (brut * (Number(l.taux_remise) || 0)) / 100 + (Number(l.remise_journee_ht) || 0) * joursFactures,
    brut
  );
  return brut - remise;
}

const euros = (n) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

/** Brouillon de l'éditeur de texte, depuis le contrat complet. */
function texteDepuis(contrat) {
  return {
    titreDoc: contrat.titre_doc || 'CONTRAT DE FORMATION PROFESSIONNELLE',
    sousTitre: contrat.sous_titre || '',
    referenceLegale: contrat.reference_legale || '',
    preambule: contrat.preambule || '',
    articles: (contrat.articles || []).map((a) => ({ ...a })),
  };
}

function ContratPanneau({
  contrat,
  onClose,
  onModifie,
  onPdf,
  onEnvoyer,
  onPlanifier,
  // 'modification' : ouvert depuis le crayon d'une liste, le formulaire
  // s'affiche dès que le contrat entier est relu (sauf contrat signé).
  modeInitial = 'apercu',
}) {
  const [mode, setMode] = useState('apercu');
  // La ligne de liste ne porte qu'une projection du contrat : plusieurs
  // champs corrigeables (lieu de signature, interlocuteur, sites) n'y sont
  // pas. On relit le contrat entier avant d'ouvrir le formulaire, faute de
  // quoi enregistrer viderait les champs jamais reçus.
  const [complet, setComplet] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [brouillon, setBrouillon] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  // Change à chaque enregistrement : force l'iframe à recharger l'aperçu,
  // sinon on relirait le contrat d'avant la correction.
  const [version, setVersion] = useState(0);

  // Texte du contrat (migration 044) : modèles proposés au choix, et
  // brouillon de l'éditeur. `texte` est relu du contrat complet à chaque
  // enregistrement, comme `brouillon` pour les mentions.
  const [modeles, setModeles] = useState([]);
  const [modeleChoisi, setModeleChoisi] = useState('');
  const [texte, setTexte] = useState(null);
  const [savingTexte, setSavingTexte] = useState(false);

  // Un contrat signé par le client est un document engageant : le serveur
  // refuse de le modifier, autant ne pas proposer le bouton.
  const modifiable = !contrat.signature_client_le;

  // Établi hors de l'application puis téléversé (Parapheur) : le fichier fait
  // foi. Il ne se modifie pas ici et ne se régénère pas.
  const externe = (complet || contrat).origine === 'externe';
  const [regeneration, setRegeneration] = useState(false);

  /** Refait le PDF du contrat à partir de ses données actuelles, puis recharge l'aperçu. */
  async function regenerer() {
    setRegeneration(true);
    setMessage(null);
    try {
      await regenererPdfContrat(contrat.id);
      setVersion((v) => v + 1);
      setMessage({ ok: true, texte: `Le PDF du contrat ${contrat.numero} a été refait.` });
    } catch (err) {
      setMessage({ ok: false, texte: err.message || 'Régénération impossible.' });
    } finally {
      setRegeneration(false);
    }
  }

  useEffect(() => {
    let vivant = true;
    setChargement(true);
    getContratById(contrat.id)
      .then((res) => {
        if (!vivant) return;
        setComplet(res.data);
        setBrouillon(brouillonDepuis(res.data));
        setTexte(texteDepuis(res.data));
        setModeleChoisi(res.data.modele_id ? String(res.data.modele_id) : '');
        if (modeInitial === 'modification' && !res.data.signature_client_le) setMode('modification');
      })
      .catch((err) => {
        if (vivant) setMessage({ ok: false, texte: err.message || 'Contrat illisible.' });
      })
      .finally(() => {
        if (vivant) setChargement(false);
      });
    return () => {
      vivant = false;
    };
  }, [contrat.id, modeInitial]);

  useEffect(() => {
    let vivant = true;
    getModelesContrat({ actif: true })
      .then((res) => {
        if (vivant) setModeles(res.data || []);
      })
      .catch(() => {
        /* sans modèles, le sélecteur reste vide : l'éditeur marche quand même */
      });
    return () => {
      vivant = false;
    };
  }, []);

  function changer(cle, valeur) {
    setBrouillon((b) => ({ ...b, [cle]: valeur }));
  }

  function changerLigne(index, cle, valeur) {
    setBrouillon((b) => ({
      ...b,
      lignes: (b.lignes || []).map((l, i) => (i === index ? { ...l, [cle]: valeur } : l)),
    }));
  }

  /** Reporte les valeurs lues par l'IA dans le brouillon, comme `changer`, sans toucher aux autres champs. */
  function appliquerValeursIA(valeurs) {
    const suite = {};
    for (const champ of CHAMPS_IA) {
      if (valeurs[champ.cle] === undefined || valeurs[champ.cle] === null) continue;
      // Les <input> du formulaire portent des chaînes ; la date doit rester aaaa-mm-jj.
      suite[champ.cle] =
        champ.type === 'date' ? versInputDate(valeurs[champ.cle]) : String(valeurs[champ.cle]);
    }
    setBrouillon((b) => ({ ...b, ...suite }));
  }

  async function enregistrer() {
    setSaving(true);
    setMessage(null);
    try {
      const res = await modifierContrat(contrat.id, brouillon);
      setComplet(res.data);
      setBrouillon(brouillonDepuis(res.data));
      setVersion((v) => v + 1);
      setMode('apercu');
      setMessage({ ok: true, texte: 'Contrat corrigé. Le PDF a été refait : les lignes à 0 € n’y figurent plus.' });
      if (onModifie) onModifie(res.data);
    } catch (err) {
      setMessage({ ok: false, texte: err.message || 'Modification impossible.' });
    } finally {
      setSaving(false);
    }
  }

  function annuler() {
    setBrouillon(brouillonDepuis(complet || contrat));
    setMessage(null);
    setMode('apercu');
  }

  /** Recopie les articles du modèle choisi dans le contrat, puis recharge l'aperçu. */
  async function appliquerModele() {
    if (!modeleChoisi) return;
    setSavingTexte(true);
    setMessage(null);
    try {
      const res = await appliquerModeleContrat(contrat.id, Number(modeleChoisi));
      setComplet(res.data);
      setTexte(texteDepuis(res.data));
      setVersion((v) => v + 1);
      setMessage({
        ok: true,
        texte: 'Modèle appliqué : retouchez le texte ci-dessous si besoin, puis enregistrez.',
      });
      if (onModifie) onModifie(res.data);
    } catch (err) {
      setMessage({ ok: false, texte: err.message || 'Application du modèle impossible.' });
    } finally {
      setSavingTexte(false);
    }
  }

  const fichierWord = useRef(null);

  async function exporterWord() {
    setSavingTexte(true);
    setMessage(null);
    try {
      const nom = await telechargerDocxContrat(contrat.id);
      setMessage({ ok: true, texte: `${nom} téléchargé : retouchez-le dans Word, puis importez-le ici.` });
    } catch (err) {
      setMessage({ ok: false, texte: err.message || 'Export Word impossible.' });
    } finally {
      setSavingTexte(false);
    }
  }

  async function importerWord(e) {
    const fichier = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!fichier) return;
    setSavingTexte(true);
    setMessage(null);
    try {
      const res = await importerDocxContrat(contrat.id, fichier);
      setComplet(res.data);
      setTexte(texteDepuis(res.data));
      setVersion((v) => v + 1);
      setMode('apercu');
      setMessage({ ok: true, texte: res.message || 'Word importé.' });
      if (onModifie) onModifie(res.data);
    } catch (err) {
      setMessage({ ok: false, texte: err.message || 'Import du Word impossible.' });
    } finally {
      setSavingTexte(false);
    }
  }

  async function retirerWord() {
    setSavingTexte(true);
    setMessage(null);
    try {
      const res = await retirerDocxContrat(contrat.id);
      setComplet(res.data);
      setVersion((v) => v + 1);
      setMessage({ ok: true, texte: res.message || 'Retour au texte du modèle.' });
      if (onModifie) onModifie(res.data);
    } catch (err) {
      setMessage({ ok: false, texte: err.message || 'Opération impossible.' });
    } finally {
      setSavingTexte(false);
    }
  }

  return (
    <div className="devis-modal-overlay" onClick={onClose}>
      <div
        className="devis-modal contrat-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="devis-modal-header">
          <div>
            <div className="devis-modal-numero">
              {contrat.numero}
              {externe && (
                <span className="badge-origine badge-origine-externe" title="Établi hors de l’application puis téléversé">
                  Externe
                </span>
              )}
            </div>
            <div className="devis-modal-sub">
              {contrat.societe_nom} ·{' '}
              {contrat.devis_numero ? `devis ${contrat.devis_numero}` : 'sans devis'} ·{' '}
              {formatDate(contrat.date_contrat)}
            </div>
          </div>

          <div className="devis-modal-header-right contrat-modal-actions">
            {mode === 'apercu' && (
              <button
                type="button"
                className="devis-btn devis-btn-sm"
                onClick={regenerer}
                disabled={chargement || regeneration || externe}
                title={
                  externe
                    ? 'Contrat établi hors de l’application : son fichier fait foi, il ne se régénère pas'
                    : 'Refaire le PDF du contrat à partir de ses données actuelles'
                }
              >
                {regeneration ? 'Régénération' : 'Régénérer le PDF'}
              </button>
            )}
            {mode === 'apercu' && modifiable && !externe && (
              <button
                type="button"
                className="devis-btn devis-btn-sm"
                onClick={() => setMode('modification')}
                disabled={chargement || !brouillon}
              >
                {chargement ? 'Lecture' : 'Modifier'}
              </button>
            )}
            {mode === 'apercu' && modifiable && !externe && (
              <button
                type="button"
                className="devis-btn devis-btn-sm"
                onClick={() => setMode('texte')}
                disabled={chargement || !texte}
                title="Choisir un modèle et retoucher le texte du contrat"
              >
                Texte
              </button>
            )}
            {mode === 'apercu' && onPlanifier && (
              <button type="button" className="devis-btn devis-btn-sm" onClick={onPlanifier}>
                Planification
              </button>
            )}
            {mode === 'apercu' && onPdf && (
              <button type="button" className="devis-btn devis-btn-sm" onClick={onPdf}>
                <IconeTelecharger />
                PDF
              </button>
            )}
            {mode === 'apercu' && onEnvoyer && !contrat.signature_client_le && (
              <button
                type="button"
                className="devis-btn devis-btn-sm devis-btn-primary"
                onClick={onEnvoyer}
                /* Même règle que la liste et le serveur : le contrat part
                   planification faite et documents produits. */
                disabled={!contrat.nb_journees || contrat.journees_incompletes > 0}
                title={
                  !contrat.nb_journees
                    ? 'Planifiez la formation avant l’envoi'
                    : contrat.journees_incompletes > 0
                      ? 'Des journées n’ont pas tous leurs documents (convocation, feuille de présence)'
                      : 'Envoyer le contrat au client pour signature'
                }
              >
                Envoyer
              </button>
            )}
            <button
              type="button"
              className="devis-drawer-close"
              onClick={onClose}
              aria-label="Fermer"
            >
              ×
            </button>
          </div>
        </div>

        {message && (
          <div
            className={`contrat-message${message.ok ? '' : ' contrat-message-erreur'}`}
          >
            {message.texte}
          </div>
        )}

        {mode === 'apercu' && (
          <div className="contrat-apercu">
            <iframe
              key={version}
              className="contrat-apercu-cadre"
              title={`Contrat ${contrat.numero}`}
              src={externe ? urlPdfContrat(contrat.id, { apercu: true }) : urlApercuContrat(contrat.id)}
            />
          </div>
        )}

        {mode === 'texte' && texte && (
          <>
            <div className="devis-modal-body contrat-form contrat-texte">
              <p className="contrat-form-note">
                Choisissez le modèle, puis téléchargez le .docx, modifiez-le et réimportez-le.
              </p>

              <div className="contrat-form-section">Modèle de contrat</div>
              <SelecteurModeleContrat
                modeles={modeles}
                valeur={modeleChoisi}
                onChoisir={setModeleChoisi}
                onAppliquer={appliquerModele}
                disabled={savingTexte}
              />

              <div className="contrat-form-section">Texte dans Word</div>
              {complet?.docx_path ? (
                <div className="contrat-texte-word contrat-texte-word-actif">
                  <div>
                    <strong>Word importé</strong>
                    {complet.docx_importe_le ? ` le ${formatDate(complet.docx_importe_le)}` : ''} — c&apos;est
                    lui qui s&apos;imprime et qui part au client.
                  </div>
                  <div className="contrat-texte-word-actions">
                    <button type="button" className="devis-btn devis-btn-sm" onClick={exporterWord} disabled={savingTexte}>
                      Télécharger le Word
                    </button>
                    <button type="button" className="devis-btn devis-btn-sm devis-btn-primary" onClick={() => fichierWord.current?.click()} disabled={savingTexte}>
                      Importer une nouvelle version
                    </button>
                    <button type="button" className="devis-btn devis-btn-sm" onClick={retirerWord} disabled={savingTexte}>
                      Revenir au modèle
                    </button>
                  </div>
                </div>
              ) : (
                <div className="contrat-texte-word">
                  <div>
                    Le contrat suit {texte.articles.length ? 'le modèle appliqué' : 'le texte standard'}.
                    Pour le retoucher, exportez-le en Word puis importez le fichier modifié.
                  </div>
                  <div className="contrat-texte-word-actions">
                    <button type="button" className="devis-btn devis-btn-sm devis-btn-primary" onClick={exporterWord} disabled={savingTexte}>
                      {savingTexte ? 'En cours' : 'Télécharger en Word'}
                    </button>
                    <button type="button" className="devis-btn devis-btn-sm" onClick={() => fichierWord.current?.click()} disabled={savingTexte}>
                      Importer le Word retouché
                    </button>
                  </div>
                </div>
              )}
              <input
                ref={fichierWord}
                type="file"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={importerWord}
                hidden
              />
            </div>

            <div className="devis-modal-footer">
              <button type="button" className="devis-btn" onClick={() => setMode('apercu')} disabled={savingTexte}>
                Fermer
              </button>
            </div>
          </>
        )}

        {mode === 'modification' && brouillon && (
          <>
            <div className="devis-modal-body contrat-form">
              <RemplirParTexte
                contexte="contrat de formation : entreprise cliente, représentant, interlocuteur, date et lieu de signature, intitulé, dates et lieu de la formation, offre exceptionnelle, TVA"
                champs={CHAMPS_IA}
                onValeurs={appliquerValeursIA}
              />

              <p className="contrat-form-note">
                Corrigé ici, un prix ne change ni le devis ni le catalogue. Prestations et
                durées : reprendre le devis.
              </p>

              <div className="contrat-form-grille">
                {SECTIONS.map((section) => (
                  <Fragment key={section.titre}>
                    <div className="contrat-form-section">{section.titre}</div>
                    {section.champs.map((champ) => (
                      <div
                        key={champ.cle}
                        className={`param-field${champ.multiligne ? ' contrat-field-large' : ''}`}
                      >
                        <label className="param-label" htmlFor={`c-${champ.cle}`}>
                          {champ.libelle}
                        </label>
                        {champ.multiligne ? (
                          <textarea
                            id={`c-${champ.cle}`}
                            className="param-input"
                            rows={2}
                            value={brouillon[champ.cle]}
                            onChange={(e) => changer(champ.cle, e.target.value)}
                            disabled={saving}
                          />
                        ) : (
                          <input
                            id={`c-${champ.cle}`}
                            className="param-input"
                            type={champ.type || 'text'}
                            value={brouillon[champ.cle]}
                            onChange={(e) => changer(champ.cle, e.target.value)}
                            disabled={saving}
                          />
                        )}
                      </div>
                    ))}
                  </Fragment>
                ))}
              </div>

              {/* Tarifs : le prix de chaque ligne, sa remise, l'offre, la TVA. */}
              <div className="contrat-form-grille contrat-tarifs">
                <div className="contrat-form-section">Tarifs du contrat</div>
                {(brouillon.lignes || []).map((l, i) => (
                  <Fragment key={i}>
                    <div className="param-field contrat-field-large contrat-tarif-libelle">
                      <span className="param-label">
                        {i + 1}. {l.libelle}
                        {l.groupe_libelle ? ` — ${l.groupe_libelle}` : ''}
                        {' '}
                        <small>
                          ({Number(l.duree_jours)} j
                          {l.unite === 'jour_participant'
                            ? ` × ${l.nb_participants} participant(s)`
                            : ' — forfait groupe'}
                          , net indicatif {euros(apercuLigneContrat(l))})
                        </small>
                      </span>
                    </div>
                    <div className="param-field">
                      <label className="param-label" htmlFor={`c-prix-${i}`}>
                        Prix unitaire HT (€)
                      </label>
                      <input
                        id={`c-prix-${i}`}
                        className="param-input"
                        type="number"
                        step="0.01"
                        min="0"
                        value={l.prix_unitaire_ht}
                        onChange={(e) => changerLigne(i, 'prix_unitaire_ht', e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="param-field">
                      <label className="param-label" htmlFor={`c-remise-${i}`}>
                        Remise (%)
                      </label>
                      <input
                        id={`c-remise-${i}`}
                        className="param-input"
                        type="number"
                        step="0.5"
                        min="0"
                        max="100"
                        value={l.taux_remise}
                        onChange={(e) => changerLigne(i, 'taux_remise', e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="param-field">
                      <label className="param-label" htmlFor={`c-remise-j-${i}`}>
                        Remise par journée (€)
                      </label>
                      <input
                        id={`c-remise-j-${i}`}
                        className="param-input"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0"
                        value={l.remise_journee_ht || ''}
                        onChange={(e) => changerLigne(i, 'remise_journee_ht', e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    {(Number(l.taux_remise) > 0 || Number(l.remise_journee_ht) > 0) && (
                      <div className="param-field">
                        <label className="param-label" htmlFor={`c-motif-${i}`}>
                          Motif de la remise
                        </label>
                        <input
                          id={`c-motif-${i}`}
                          className="param-input"
                          type="text"
                          placeholder="Remise commerciale"
                          value={l.motif_remise}
                          onChange={(e) => changerLigne(i, 'motif_remise', e.target.value)}
                          disabled={saving}
                        />
                      </div>
                    )}
                  </Fragment>
                ))}
                <div className="param-field">
                  <label className="param-label" htmlFor="c-offre-libelle">
                    Offre exceptionnelle (libellé)
                  </label>
                  <input
                    id="c-offre-libelle"
                    className="param-input"
                    type="text"
                    placeholder="Offre de bienvenue, 2e journée offerte…"
                    value={brouillon.offre_libelle}
                    onChange={(e) => changer('offre_libelle', e.target.value)}
                    disabled={saving}
                  />
                </div>
                <div className="param-field">
                  <label className="param-label" htmlFor="c-offre-ht">
                    Montant de l’offre (€ HT)
                  </label>
                  <input
                    id="c-offre-ht"
                    className="param-input"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0"
                    value={brouillon.offre_ht}
                    onChange={(e) => changer('offre_ht', e.target.value)}
                    disabled={saving}
                  />
                </div>
                <div className="param-field">
                  <label className="param-label" htmlFor="c-tva">
                    TVA (%)
                  </label>
                  <input
                    id="c-tva"
                    className="param-input"
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={brouillon.taux_tva}
                    onChange={(e) => changer('taux_tva', e.target.value)}
                    disabled={saving}
                  />
                </div>
              </div>
            </div>

            <div className="devis-modal-footer">
              <button
                type="button"
                className="devis-btn"
                onClick={annuler}
                disabled={saving}
              >
                Annuler
              </button>
              <button
                type="button"
                className="devis-btn devis-btn-primary"
                onClick={enregistrer}
                disabled={saving}
              >
                {saving ? 'Enregistrement' : 'Enregistrer'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ContratPanneau;
